// Copyright 2026, Imprivata, Inc.  All rights reserved.

import { prepareAppProfileUpload } from '@api/portalServices';
import { uploadFile } from '@api/setupServices';
import { Button } from '@imprivata-cloud/components';
import { message, Upload } from 'antd';
import type { RcFile } from 'antd/es/upload';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { TRANSLATION_ROOT } from './AppProfiles.types';
import { useImportAppProfileMutation } from './useAppProfiles';

// Browsers report XML MIME types inconsistently (text/xml, application/xml,
// occasionally application/octet-stream) — the file-extension filter is the
// real gate, MIME is a UX hint only.
const ACCEPTED_APP_PROFILE_FILE_TYPES = '.xml,text/xml,application/xml';

// `accept` only filters the browser's file picker — it doesn't stop a
// drag-drop or an "All files" selection, so `handleUpload` re-checks the
// extension itself before doing any network work.
const isXmlFile = (fileName: string): boolean => fileName.toLowerCase().endsWith('.xml');

interface AppProfilesImportButtonProps {
  children?: ReactNode;
  disabled?: boolean;
}

export const AppProfilesImportButton = ({ children, disabled }: AppProfilesImportButtonProps) => {
  const { t } = useTranslation();
  const [messageApi, contextHolder] = message.useMessage();
  const { mutateAsync: importAppProfile } = useImportAppProfileMutation();

  const handleUpload = async (file: RcFile): Promise<boolean> => {
    if (!isXmlFile(file.name)) {
      void messageApi.error(t(`${TRANSLATION_ROOT}.import.invalid-file-error`));
    } else {
      try {
        const prepared = await prepareAppProfileUpload(file.name);
        await uploadFile(file, prepared.uploadUrl);
        try {
          await importAppProfile({ readUrl: prepared.readUrl });
          void messageApi.success(t(`${TRANSLATION_ROOT}.import.success`));
        } catch {
          void messageApi.error(t(`${TRANSLATION_ROOT}.import.import-error`));
        }
      } catch {
        void messageApi.error(t(`${TRANSLATION_ROOT}.import.upload-error`));
      }
    }
    return false;
  };

  return (
    <>
      {contextHolder}
      <Upload
        accept={ACCEPTED_APP_PROFILE_FILE_TYPES}
        showUploadList={false}
        disabled={disabled}
        beforeUpload={handleUpload}
      >
        {children ?? <Button disabled={disabled}>{t(`${TRANSLATION_ROOT}.import.cta`)}</Button>}
      </Upload>
    </>
  );
};
